import React, { useEffect, useState } from "react";
import { TextInput, Button, Label, Avatar, Textarea } from "flowbite-react";
import {
  HiCheck,
  HiChevronLeft,
  HiChevronRight,
  HiOutlineX,
  HiOutlineZoomIn,
  HiOutlineZoomOut,
} from "react-icons/hi";
import { FaMapMarkerAlt, FaWhatsapp } from "react-icons/fa";
import Cropper from "react-easy-crop";
import getCroppedImg from "../../utils";
import { getStoreProfilePictureUrl } from "../../utils/storeProfilePicture";

const ACCEPTED_PROFILE_PICTURE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

const CARD_LAYOUT_OPTIONS = [
  {
    value: "classico",
    title: "Classico branco",
    image: "/mockups/card-produto-classico-thumb.png",
  },
  {
    value: "vitrine",
    title: "Vitrine imersiva",
    image: "/mockups/card-produto-vitrine-thumb.png",
  },
];

const LojaForm = ({
  lojaForm,
  handleLojaChange,
  handleSubmitLoja,
  profilePicture,
  onProfilePictureUpdate,
  canEdit = true,
  section = "all",
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [error, setError] = useState("");
  const [isEditingPhoto, setIsEditingPhoto] = useState(false);
  const [showMetaAdvanced, setShowMetaAdvanced] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("/store.png");

  useEffect(() => {
    if (!(profilePicture instanceof Blob)) {
      setPreviewUrl(
        getStoreProfilePictureUrl(
          profilePicture,
          lojaForm.updatedAt || lojaForm.updated_at
        )
      );
      return undefined;
    }

    const objectUrl = URL.createObjectURL(profilePicture);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [profilePicture, lojaForm.updatedAt, lojaForm.updated_at]);

  useEffect(
    () => () => {
      if (selectedFile) {
        URL.revokeObjectURL(selectedFile);
      }
    },
    [selectedFile]
  );

  const handleFileChange = (e) => {
    if (!canEdit) return;

    const file = e.target.files[0];
    e.target.value = "";

    if (file && ACCEPTED_PROFILE_PICTURE_TYPES.includes(file.type)) {
      if (file.size > 5 * 1024 * 1024) {
        setError("Por favor, selecione uma imagem menor que 5MB.");
        return;
      }
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
      img.onload = () => {
        if (img.width === img.height) {
          onProfilePictureUpdate(file);
          setIsEditingPhoto(false);
          URL.revokeObjectURL(objectUrl);
        } else {
          setSelectedFile(objectUrl);
          setIsEditingPhoto(true);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        setError("Nao foi possivel ler a imagem selecionada.");
      };
      setError("");
    } else {
      setError("Selecione uma imagem JPG, PNG ou WebP.");
    }
  };

  const onCropComplete = (croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const handleCropSave = async () => {
    if (!canEdit) return;
    if (!croppedAreaPixels) {
      setError("Aguarde a imagem carregar antes de salvar o corte.");
      return;
    }

    try {
      const blob = await getCroppedImg(selectedFile, croppedAreaPixels, 300);
      if (!blob) {
        throw new Error("Nao foi possivel gerar a imagem cortada.");
      }
      onProfilePictureUpdate(blob);
      setIsEditingPhoto(false);
      setSelectedFile(null);
      setError("");
    } catch (err) {
      console.error("Erro ao cortar a imagem:", err);
      setError("Nao foi possivel cortar a imagem. Tente novamente.");
    }
  };

  const handleLayoutCardChange = (value) => {
    handleLojaChange({
      target: {
        name: "layout_card_produto",
        value,
      },
    });
  };

  const layoutCardProduto = lojaForm.layout_card_produto || "classico";

  return (
    <form className="mt-10 space-y-8" onSubmit={handleSubmitLoja}>
      <fieldset disabled={!canEdit} className={!canEdit ? "opacity-70" : ""}>
        {section !== "seo" ? (
          <>
            <div className="flex flex-col items-center space-y-4">
              <Avatar
                img={previewUrl}
                alt="Foto de perfil da loja"
                rounded
                size="xl"
              />
              <label htmlFor="foto" className="cursor-pointer">
                <Button
                  gradientDuoTone="purpleToPink"
                  type="button"
                  onClick={() => document.getElementById("file-input").click()}
                >
                  Editar Foto de Perfil da Loja
                </Button>
              </label>
              <input
                id="file-input"
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
                className="hidden"
              />
              {error && <p className="text-red-500 text-sm">{error}</p>}
            </div>

            {isEditingPhoto && selectedFile && (
              <>
                <div className="relative w-full h-72 mt-6">
                  <Cropper
                    image={selectedFile}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
                <div className="flex justify-between items-center mt-4">
                  <Button
                    gradientDuoTone="purpleToPink"
                    type="button"
                    size="sm"
                    onClick={() => setZoom((prev) => Math.max(prev - 0.1, 1))}
                    className="flex items-center space-x-2"
                  >
                    <HiOutlineZoomOut className="mr-2" />
                    Zoom Out
                  </Button>
                  <Button
                    gradientDuoTone="purpleToPink"
                    type="button"
                    size="sm"
                    onClick={() => setZoom((prev) => Math.min(prev + 0.1, 3))}
                    className="flex items-center space-x-2"
                  >
                    <HiOutlineZoomIn className="mr-2" />
                    Zoom In
                  </Button>
                </div>
                <div className="flex flex-col sm:flex-row justify-end sm:space-x-2 space-y-2 sm:space-y-0 mt-4">
                  <Button
                    gradientDuoTone="purpleToPink"
                    type="button"
                    size="sm"
                    onClick={handleCropSave}
                  >
                    Salvar Corte
                  </Button>
                  <Button
                    gradientDuoTone="purpleToPink"
                    type="button"
                    size="sm"
                    onClick={() => {
                      setIsEditingPhoto(false);
                      setSelectedFile(null);
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="nome_fantasia" className="mb-2 block">
                Nome Fantasia
              </Label>
              <TextInput
                id="nome_fantasia"
                name="nome_fantasia"
                type="text"
                placeholder="Digite o nome da loja"
                value={lojaForm.nome_fantasia || ""}
                onChange={handleLojaChange}
              />
            </div>

            <div>
              <Label htmlFor="slogan" className="mb-2 block">
                Slogan
              </Label>
              <TextInput
                id="slogan"
                name="slogan"
                type="text"
                placeholder="Ex.: Moda em movimento; Vista sua essencia"
                value={lojaForm.slogan || ""}
                onChange={handleLojaChange}
              />
            </div>

            <div>
              <Label
                id="layout-card-produto-label"
                htmlFor="layout_card_produto"
                className="mb-2 block"
              >
                Card de produto no catalogo
              </Label>
              <div
                id="layout_card_produto"
                className="grid gap-4 sm:grid-cols-2"
                role="radiogroup"
                aria-labelledby="layout-card-produto-label"
              >
                {CARD_LAYOUT_OPTIONS.map((option) => {
                  const selected = layoutCardProduto === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={!canEdit}
                      onClick={() => handleLayoutCardChange(option.value)}
                      className={`group overflow-hidden rounded-xl border bg-white text-left shadow-sm transition focus:outline-none focus:ring-4 focus:ring-purple-200 disabled:cursor-not-allowed ${
                        selected
                          ? "border-purple-500 ring-2 ring-purple-500"
                          : "border-gray-200 hover:border-purple-300"
                      }`}
                    >
                      <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
                        {option.value === "classico" ? (
                          <div className="absolute inset-0 bg-slate-200/70 p-2.5">
                            <div className="h-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-200">
                              <div className="flex h-8 items-center justify-between border-b border-gray-100 px-3">
                                <span className="truncate text-[10px] font-semibold text-slate-800">
                                  Camiseta Preta com Flamingo
                                </span>
                                <span className="grid h-5 w-5 flex-shrink-0 place-items-center rounded-md border border-slate-400 text-slate-500">
                                  <HiOutlineX className="h-3.5 w-3.5" />
                                </span>
                              </div>

                              <div className="grid h-[calc(100%-2rem)] grid-cols-[0.95fr_1.05fr] gap-3 p-3">
                                <div className="flex min-w-0 flex-col items-center justify-between gap-2">
                                  <div className="flex h-[7.3rem] w-full items-center justify-center overflow-hidden rounded-md bg-gray-50 shadow-sm">
                                    <img
                                      src={option.image}
                                      alt=""
                                      className="h-full max-w-full object-contain"
                                    />
                                  </div>

                                  <div className="flex w-full gap-1.5">
                                    <span className="h-7 w-5 overflow-hidden rounded border border-slate-800 bg-slate-100">
                                      <img
                                        src={option.image}
                                        alt=""
                                        className="h-full w-full object-cover object-top"
                                      />
                                    </span>
                                    <span className="h-7 w-5 overflow-hidden rounded bg-slate-100 opacity-75">
                                      <img
                                        src="/mockups/card-produto-vitrine-thumb.png"
                                        alt=""
                                        className="h-full w-full object-cover object-top"
                                      />
                                    </span>
                                  </div>
                                </div>

                                <div className="flex min-w-0 flex-col gap-2 pt-1">
                                  <div className="flex gap-1.5">
                                    <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[8px] font-semibold text-slate-700">
                                      URBANA
                                    </span>
                                    <span className="rounded-full bg-black px-2 py-0.5 text-[8px] font-bold text-white">
                                      10% OFF
                                    </span>
                                  </div>
                                  <span className="w-max rounded-full bg-emerald-100 px-2.5 py-1 text-[9px] font-bold text-emerald-800">
                                    10x de R$ 49,90
                                  </span>
                                  <span className="h-px w-full bg-gray-200" />
                                  <div className="mt-auto grid grid-cols-[1.25fr_0.85fr] gap-2">
                                    <span className="flex h-7 items-center justify-center gap-1 rounded-full bg-emerald-700 px-2 text-[8px] font-bold text-white">
                                      <FaWhatsapp className="h-2.5 w-2.5" />
                                      WhatsApp
                                    </span>
                                    <span className="flex h-7 items-center justify-center gap-1 rounded-full border border-slate-200 bg-white px-2 text-[8px] font-semibold text-slate-800">
                                      <FaMapMarkerAlt className="h-2.5 w-2.5" />
                                      Loja
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <img
                              src={option.image}
                              alt=""
                              className="absolute inset-0 h-full w-full scale-110 object-cover object-top blur-md"
                            />
                            <div className="absolute inset-0 bg-slate-950/45" />
                            <div className="absolute inset-y-0 left-1/2 w-[34%] -translate-x-1/2 overflow-hidden bg-black/10">
                              <img
                                src={option.image}
                                alt=""
                                className="h-full w-full object-cover object-top"
                              />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-transparent" />
                            <div className="absolute left-3 top-3 rounded-full bg-white/25 px-2 py-1 text-[9px] font-bold text-white backdrop-blur">
                              01 / 02
                            </div>
                            <div className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full bg-white/25 text-white backdrop-blur">
                              <HiOutlineX className="h-4 w-4" />
                            </div>
                            <div className="absolute left-3 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white/20 text-white">
                              <HiChevronLeft className="h-5 w-5" />
                            </div>
                            <div className="absolute right-3 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white/20 text-white">
                              <HiChevronRight className="h-5 w-5" />
                            </div>
                            <div className="absolute inset-x-5 bottom-4 grid grid-cols-[minmax(0,1fr)_8rem] items-end gap-4">
                              <div>
                                <div className="mb-2 flex gap-1">
                                  <span className="h-1.5 w-7 rounded-full bg-white" />
                                  <span className="h-1.5 w-2 rounded-full bg-white/40" />
                                </div>
                                <div className="mb-1.5 flex gap-1.5">
                                  <span className="rounded-full border border-white/35 bg-white/15 px-1.5 py-0.5 text-[8px] font-bold text-white">
                                    URBANA
                                  </span>
                                  <span className="rounded-full bg-white px-1.5 py-0.5 text-[8px] font-bold text-slate-900">
                                    10% OFF
                                  </span>
                                </div>
                                <span className="mb-1 block truncate text-[11px] font-bold leading-tight text-white">
                                  Camiseta Preta com Flamingo
                                </span>
                                <span className="block text-[9px] font-bold text-white">
                                  R$ 449,10
                                </span>
                              </div>
                              <div className="grid gap-2">
                                <span className="flex h-7 items-center justify-center gap-1 rounded-full bg-white/25 px-2 text-[8px] font-bold text-white ring-1 ring-white/35">
                                  <FaWhatsapp className="h-2.5 w-2.5" />
                                  WhatsApp
                                </span>
                                <span className="flex h-7 items-center justify-center gap-1 rounded-full bg-white/10 px-2 text-[8px] font-bold text-white ring-1 ring-white/25">
                                  <FaMapMarkerAlt className="h-2.5 w-2.5" />
                                  Loja
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                        <div
                          className={`absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full border text-sm font-bold transition ${
                            selected
                              ? "border-purple-500 bg-purple-600 text-white"
                              : "border-white/80 bg-white/85 text-transparent"
                          }`}
                          aria-hidden="true"
                        >
                          {selected ? <HiCheck className="h-4 w-4" /> : null}
                        </div>
                      </div>

                      <div className="px-4 py-3">
                        <span className="block text-sm font-semibold text-gray-900">
                          {option.title}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}

        {section !== "basic" ? (
          <>
            <h2 className="text-lg font-semibold text-gray-700 text-center">
              SEO e Codigos de Rastreamento
            </h2>

            <div>
              <Label htmlFor="seo_title" className="mb-2 block">
                Titulo para o Google
              </Label>
              <TextInput
                id="seo_title"
                name="seo_title"
                type="text"
                placeholder="Ex: Moda Lari | Vitrine Virtual"
                value={lojaForm.seo_title || ""}
                onChange={handleLojaChange}
              />
            </div>

            <div>
              <Label htmlFor="seo_description" className="mb-2 block">
                Descricao para o Google e redes sociais
              </Label>
              <TextInput
                id="seo_description"
                name="seo_description"
                type="text"
                placeholder="Ex: Looks de verao, moda praia e tendencias"
                value={lojaForm.seo_description || ""}
                onChange={handleLojaChange}
              />
            </div>

            <div>
              <Label htmlFor="seo_keywords" className="mb-2 block">
                Palavras-chave (separadas por virgula)
              </Label>
              <TextInput
                id="seo_keywords"
                name="seo_keywords"
                type="text"
                placeholder="moda praia, biquini, verao"
                value={lojaForm.seo_keywords || ""}
                onChange={handleLojaChange}
              />
            </div>

            <div>
              <Label htmlFor="custom_head_code" className="mb-2 block">
                Codigo personalizado para o cabecalho do catalogo
              </Label>
              <Textarea
                id="custom_head_code"
                name="custom_head_code"
                rows={8}
                placeholder={`Cole aqui scripts/meta/link (ex.: Pixel, GTM, GA, Ads).\nEsse codigo sera injetado no <head> da pagina publica do catalogo.`}
                value={lojaForm.custom_head_code || ""}
                onChange={handleLojaChange}
              />
              <p className="mt-2 text-sm text-gray-500">
                Use apenas codigo confiavel. Esse conteudo roda no navegador de quem
                visita seu catalogo.
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
              <button
                type="button"
                onClick={() => setShowMetaAdvanced((prev) => !prev)}
                className="w-full flex items-center justify-between text-left"
              >
                <h3 className="text-sm font-semibold text-gray-700">
                  Meta Pixel e CAPI (Avancado)
                </h3>
                <span className="text-xs text-gray-500">
                  {showMetaAdvanced ? "Ocultar" : "Expandir"}
                </span>
              </button>

              {showMetaAdvanced ? (
                <>
                  <div>
                    <Label htmlFor="pixel_meta_id" className="mb-2 block">
                      Pixel ID
                    </Label>
                    <TextInput
                      id="pixel_meta_id"
                      name="pixel_meta_id"
                      type="text"
                      placeholder="Ex.: 123456789012345"
                      value={lojaForm.pixel_meta_id || ""}
                      onChange={handleLojaChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="pixel_meta_access_token" className="mb-2 block">
                      Access Token (CAPI)
                    </Label>
                    <TextInput
                      id="pixel_meta_access_token"
                      name="pixel_meta_access_token"
                      type="password"
                      autoComplete="off"
                      placeholder="Token do pixel para envio server-side"
                      value={lojaForm.pixel_meta_access_token || ""}
                      onChange={handleLojaChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="pixel_meta_test_event_code" className="mb-2 block">
                      Test Event Code (opcional)
                    </Label>
                    <TextInput
                      id="pixel_meta_test_event_code"
                      name="pixel_meta_test_event_code"
                      type="text"
                      placeholder="Ex.: TEST12345"
                      value={lojaForm.pixel_meta_test_event_code || ""}
                      onChange={handleLojaChange}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Com Pixel ID apenas: browser tracking. Com Token: ativa tambem CAPI.
                  </p>
                </>
              ) : null}
            </div>
          </>
        ) : null}

        <Button
          type="submit"
          gradientDuoTone="purpleToPink"
          size="lg"
          className="w-full mt-2"
          disabled={!canEdit}
        >
          {section === "seo" ? "Salvar SEO e Codigos" : "Salvar Informacoes da Loja"}
        </Button>
      </fieldset>
    </form>
  );
};

export default LojaForm;
